import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { FilePanelService } from '@lib/components/document-viewer/file-viewer/file-panel.service';
import { ConfirmationSimpleComponent } from '@lib/components/confirmation-simple/confirmation-simple.component';
import { GedDocumentType, SignatureState } from '@lib/models/generated/graphql';
import { DocumentsService } from '@lib/services/documents.service';
import { LoadingService } from '@lib/services/loading.service';
import { OuiDialogService } from 'omnium-ui/dialog';
import { OuiSnackbarService } from 'omnium-ui/snackbar';
import { SHARED_IMPORTS } from 'projects/minotaure-lib/src/shared-imports';
import { AuthService } from '@lib/services/auth-service.service';

@Component({
  selector: 'app-file-viewer',
  imports: [SHARED_IMPORTS],
  templateUrl: './file-viewer.component.html',
  styleUrl: './file-viewer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FileViewerComponent {
  public filePanelService = inject(FilePanelService);
  public documentService = inject(DocumentsService);
  public loadingService = inject(LoadingService);
  public dialogService = inject(OuiDialogService);
  public snackbarService = inject(OuiSnackbarService);
  private authService = inject(AuthService);
  public isReadOnly = computed(() => this.authService.isBackOffice());
  public fileMetadata = computed(() => this.filePanelService.fileMetadata());
  public fileUrls = computed<string[]>(() => [this.fileMetadata()?.url as string]);
  public signatureInProgress = computed<boolean>(() => this.fileMetadata()?.etatSignature === SignatureState.Pending);
  public deleteForbidden = computed<boolean>(
    () =>
      (this.fileMetadata()?.etatSignature !== null && this.fileMetadata()?.isHandwrittenSignature === false) ||
      (this.connaissanceClientDocTypes.includes(this.fileMetadata()?.typeDocumentCode as string) &&
        this.fileMetadata()?.isHandwrittenSignature === true) ||
      this.fileMetadata()?.typeDocumentCode === GedDocumentType.Cni
  );
  public connaissanceClientDocTypes = [
    GedDocumentType.RecInfo.toString(),
    GedDocumentType.Der.toString(),
    GedDocumentType.ProfInvest.toString(),
  ];

  public async downloadDocument(): Promise<void> {
    await this.documentService.downloadDocument({
      url: this.fileMetadata()?.url as string,
      name: this.fileMetadata()?.fileName as string,
    });
  }

  public openDialogDeleteDocument(): void {
    const dialogRef = this.dialogService.openDialog(ConfirmationSimpleComponent, {
      title: 'Supprimer un document',
      message: 'Souhaitez-vous réellement supprimer ce document ?',
      validateButtonLabel: 'Valider',
    });

    dialogRef.afterClosed().subscribe(async deleteDocument => {
      if (deleteDocument) {
        this.filePanelService.closeFileViewer();
        this.loadingService.startLoading();
        const result = await this.documentService.deleteDocument(this.fileMetadata()?.fileId?.split('#')[0] as string);
        this.loadingService.stopLoading();
        if (result) {
          this.snackbarService.open('Le document a été supprimé', 'success');
          this.documentService.triggerRefreshData();
        } else {
          this.snackbarService.open('Erreur lors de la suppression du document', 'error');
        }
      }
    });
  }
}
